import { useState, useEffect } from "react";
import { SquarePlus } from "lucide-react";
import AcademicCard from "./academics/AcademicCard";
import AddEducationModal from "./academics/AddEducationModal";
import {
  getCourseLevel,
  saveAcademicDetails,
  updateAcademicDetails,
} from "../api/AcademicAPI";
import SuccessDialog from "./common/SuccessDialog";
import { Snackbar, Alert } from "@mui/material";
import { authFetch } from "../../api";

const Academics = ({
  academicData: initialData,
  isEditing,
  setIsEditing,
  onSaveSuccess,
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentEditData, setCurrentEditData] = useState(null);
  const [allLevels, setAllLevels] = useState([]);
  const [academicData, setAcademicData] = useState(initialData || []);

  const [showSuccess, setShowSuccess] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const user_id = localStorage.getItem("userId");

  useEffect(() => {
    setAcademicData(initialData || []);
  }, [initialData]);

  const fetchAllevels = async () => {
    try {
      const res = await getCourseLevel();
      if (res?.success) {
        setAllLevels(res.data || []);
      }
    } catch (err) {
      console.error("Error fetching levels:", err);
    }
  };

  useEffect(() => {
    fetchAllevels();
  }, []);

  /** ------------------------
   * Add Education
   * ----------------------- */
  const handleAddEducation = async (newEducation) => {
    const newAcademic = {
      user_id: user_id,
      gl_course_level: newEducation.level,
      gl_qual_name: newEducation.qualificationName,
      institute_name: newEducation.institutionName,
      number_of_backlogs: newEducation.numberOfBacklogs,
      gl_stream_id: newEducation.stream,
      duration: newEducation.duration,
      edu_status: newEducation.educationStatus,
      marks_type: newEducation.scoreType,
      percentage: newEducation.percentage,
      year_of_passing: newEducation.yearOfPassing,
      institute_id: newEducation.institutionId,
      gl_affiliation_id: newEducation.affiliationId,
      country_id: newEducation.country,
      state_id: newEducation.state,
      city_id: newEducation.city,
      score_type: newEducation.scoreType,
    };

    try {
      const res = await saveAcademicDetails(newAcademic);
      if (res?.success) {
        setShowAddModal(false);
        setShowSuccess(true);
      } else {
        setSnackbar({
          open: true,
          message: res?.message || "Failed to save academic details",
          severity: "error",
        });
      }
    } catch (err) {
      console.error("Save error:", err);
      setSnackbar({
        open: true,
        message: "Something went wrong. Please try again.",
        severity: "error",
      });
    }
  };

  /** ------------------------
   * Edit Education
   * ----------------------- */
  const handleEditSave = async (updatedFormData, id) => {
    const updatedDetails = {
      id,
      user_id: user_id,
      // gl_course_level: updatedFormData.level,
      number_of_backlogs: updatedFormData.numberOfBacklogs,
      gl_qual_name: updatedFormData.qualificationName,
      institute_name: updatedFormData.institutionName,
      gl_stream_id: updatedFormData.stream,
      duration: updatedFormData.duration,
      edu_status: updatedFormData.educationStatus,
      marks_type: updatedFormData.scoreType,
      percentage: updatedFormData.percentage,
      year_of_passing: updatedFormData.yearOfPassing,
      institute_id: updatedFormData.institutionId,
      gl_affiliation_id: updatedFormData.affiliationId,
      country_id: updatedFormData.country,
      state_id: updatedFormData.state,
      city_id: updatedFormData.city,
      score_type: updatedFormData.scoreType,
    };

    try {
      const res = await updateAcademicDetails(updatedDetails);
      if (res?.success) {
        setShowEditModal(false);
        setCurrentEditData(null);
        setShowSuccess(true);
      } else {
        setSnackbar({
          open: true,
          message: res?.message || "Failed to update academic details",
          severity: "error",
        });
      }
    } catch (err) {
      console.error("Update error:", err);
      setSnackbar({
        open: true,
        message: "Something went wrong. Please try again.",
        severity: "error",
      });
    }
  };

  const handleModalSave = (formData, editData = null) => {
    if (editData) {
      handleEditSave(formData, editData.id);
    } else {
      handleAddEducation(formData);
    }
  };

  const handleCardEdit = (academic) => {
    setCurrentEditData(academic);
    setShowEditModal(true);
  };

  const handleCardDelete = async (academicId) => {
    try {
      const response = await authFetch("profile/delete-academic-details", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: academicId }),
      });
      // const data = await response.json();
    } catch (err) {
      console.error("Delete error:", err);
    }
    setAcademicData((prevData) =>
      prevData.filter((academic) => academic.id !== academicId)
    );
    window.location.reload();
  };

  const existingLevels = academicData.map((a) => a.gl_course_level_id);

  return (
    <div>
      <div className="relative">
        <div className="flex items-center justify-between mb-6">
          <div className="text-2xl font-semibold font-inter text-[#572575] ">
            Test Academic Details
          </div>
          <button
            className="text-sm text-white bg-[#9D0385] px-4 py-2 rounded-xl font-inter"
            onClick={() => setShowAddModal(true)}
          >
            <span className="flex gap-2">
              <SquarePlus /> Add Education Info
            </span>
          </button>
        </div>
        {/* <div className="absolute bottom-0 right-0 mb-4 mr-4">
          <button
            className="text-sm text-white bg-[#9D0385] px-4 py-2 rounded-xl font-altisunisa"
            onClick={() => setShowAddModal(true)}
          >
            <span className="flex gap-2">
              <SquarePlus /> Add Education Info
            </span>
          </button>
        </div> */}
        {academicData.map((academic) => (
          <AcademicCard
            key={academic.id}
            academic={academic}
            onEdit={handleCardEdit}
            onDelete={handleCardDelete}
          />
        ))}
      </div>

      {/* Add Modal */}
      <AddEducationModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleModalSave}
        existingLevels={existingLevels}
        allLevels={allLevels}
        academicData={academicData}
      />

      {/* Edit Modal */}
      <AddEducationModal
        open={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setCurrentEditData(null);
        }}
        onSave={handleModalSave}
        existingLevels={existingLevels}
        allLevels={allLevels}
        editData={currentEditData}
      />

      {/* Success Dialog */}
      <SuccessDialog
        open={showSuccess}
        message="Your academic details have been saved successfully."
        onClose={() => setShowSuccess(false)}
        onSuccess={() => {
          setShowSuccess(false);
          if (onSaveSuccess) onSaveSuccess();
        }}
      />

      {/* Snackbar for errors */}
      {/* Snackbar for messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Academics;
